import React, { useState, useContext } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import { ContainerSpinner } from '../../components/Spinner';
import { Button } from '../../shadcn/Button';
import { ArrowLeft, CircleCheck, KeyRound, Receipt, Trash2 } from 'lucide-react';
import Stepper from '../../components/Stepper';
import Input from '../../components/Input';
import Notice from '../../components/Notice';
import { callAPI } from '../../hooks/api';
import { TwoFactorAuthV2 } from '../../components/2FA';
import { format_identification_with_prefix } from '../../utils/formatters';

const DeletePayment = () => {
	const { authenticated, userData, CountrySettings } = useContext(GlobalContext);
	const country = userData?.country;

	const [page, setPage] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const [confNumber, setConfNumber] = useState('');
	const [identificationNumber, setIdentificationNumber] = useState('');
	const [authCodeForAuthCode, setAuthCodeForAuthCode] = useState('');
	const [userEmail, setUserEmail] = useState('');
	const [codeSent, setCodeSent] = useState(false);

	const sendAction = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setErrorMessage('');
		if (!confNumber || !authCodeForAuthCode) {
			setErrorMessage('Llene todos los datos para continuar');
			return;
		}
		setIsLoading(true);
		try {
			const [, error] = await callAPI(
				'https://api.krece.app/delete_payment_with_code/',
				'POST',
				{
					confirmation_code: authCodeForAuthCode,
					confirmation_number: confNumber,
					master_store_email: userData!.master_store.id,
					country: country || ''
				},
				userData,
				country
			);

			if (error) {
				setErrorMessage(error);
			} else {
				setErrorMessage('');
				setPage(4);
			}
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
		}
	};

	const checkCedula = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setErrorMessage('');
		if (!identificationNumber) {
			setErrorMessage('Llene todos los datos para continuar');
			return;
		}
		setIsLoading(true);
		try {
			const endpoint = `https://api.krece.app/users/${format_identification_with_prefix(identificationNumber, CountrySettings?.uid_prefix || '')}/` as 'https://api.krece.app/users/<email_or_identification_number>/';
			const [data, error] = await callAPI(endpoint, 'GET', undefined, userData, country);
			
			if (error) {
				setErrorMessage(error || 'Ocurrió un error.');
			} else if (data && data.email) {
				setUserEmail(data.email);
				setErrorMessage('');
				setPage(2);
			} else {
				setErrorMessage(`No encontramos al cliente asociado a ${country === 'peru' ? 'este DNI' : 'esta cédula'}. Por favor contactar a soporte técnico.`);
			}
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
		}
	};

	const verifyPaymentAndContinue = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setErrorMessage('');
		if (!confNumber) {
			setErrorMessage('Llene todos los datos para continuar');
			return;
		}
		setIsLoading(true);
		try {
			const [, error] = await callAPI(
				'https://api.krece.app/registration/is_valid_id/',
				'POST',
				{ id: confNumber, object_type: 'Transactions' },
				userData,
				country
			);
			
			if (error) {
				setErrorMessage(typeof error === 'string' ? error : 'Ocurrió un error al verificar el número de confirmación');
			} else {
				setErrorMessage('');
				setPage(3);
			}
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
		}
	};

	const steps = [
		{ label: 'Cédula', pages: [1] },
		{ label: 'Pago', pages: [2] },
		{ label: 'Verificación', pages: [3] },
		{ label: 'Eliminación Exitosa', pages: [4] }
	];

	if (!authenticated || !userData)
		return <ContainerSpinner size={3} />;

	return (
		<div className="m-auto flex min-h-full min-w-full flex-col items-center justify-start">
			<Stepper currentPage={page} steps={steps} />
			<div className="m-auto flex h-full w-full flex-col items-center justify-center overflow-x-hidden p-4 md:p-6">
				<div className="w-[500px] max-w-full rounded-xl border-[1.5px] border-neutral-900 bg-neutral-1000/25 p-6 shadow-lg duration-300 animate-in fade-in slide-in-from-bottom-5 light:bg-white light:shadow-none md:bg-background/50 md:p-8">
					{page === 1 && (
						<>
							{render_page_header('Eliminar Pago', `Por favor ingrese ${country === 'peru' ? 'el DNI' : 'la cédula'} del cliente para eliminar el pago`, <Trash2 />)}

							<form className="space-y-4" onSubmit={checkCedula}>
								<Input
									name="cedula"
									floatingLabel={`${country === 'peru' ? 'DNI' : 'Cédula'} del cliente`}
									value={identificationNumber}
									onChange={(value) => setIdentificationNumber(value.toLowerCase().trim())}
									placeholder={`Ingrese ${country === 'peru' ? 'el DNI' : 'la cédula'} del cliente`}
									allowedChars="numbers_only"
								/>

								{errorMessage && <Notice message={errorMessage} variant="error" />}

								<Button disabled={isLoading || !identificationNumber} loading={isLoading} type="submit" variant="primary">
									Continuar
								</Button>
							</form>
						</>
					)}

					{page === 2 && (
						<>
							{render_page_header('Pago', 'Por favor ingrese el número de confirmación del pago que desea eliminar', <Receipt />)}

							<form className="space-y-4" onSubmit={verifyPaymentAndContinue}>
								<Input
									floatingLabel="Número de confirmación de pago"
									value={confNumber}
									onChange={(value) => setConfNumber(value)}
									placeholder="Ingrese el número de confirmación"
								/>

								{errorMessage && <Notice message={errorMessage} variant="error" />}

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										disabled={isLoading || !confNumber}
										loading={isLoading}
										type="submit"
										variant="primary"
									>
										Continuar
									</Button>

									<Button
										Icon={<ArrowLeft className="size-5" />}
										disabled={isLoading}
										type="button"
										onClick={() => (window.location.href = '/tiendas/eliminar-pago')}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}

					{page === 3 && (
						<>
							{render_page_header(
								'Verificación',
								'Enviaremos un código de verificación al cliente propietario del pago',
								<KeyRound />
							)}

							<div className="space-y-4">
								<TwoFactorAuthV2
									methods={[
										{
											type: 'email',
											email: userEmail,
											allowEmailInput: false
										},
										{
											type: 'in_app',
											identificationNumber: identificationNumber,
											label: 'Notificación Push'
										}
									]}
									country={country || ''}
									confirmCode={false}
									onCodeSent={() => {
										setCodeSent(true);
										setErrorMessage('');
									}}
									onSuccess={() => {
										setCodeSent(true);
									}}
								/>

								{codeSent && (
									<form className="space-y-4" onSubmit={sendAction}>
										<Input
											floatingLabel="Código de verificación"
											value={authCodeForAuthCode}
											onChange={(value) => setAuthCodeForAuthCode(value)}
											placeholder="Ingrese el código recibido"
											allowedChars="numbers_only"
											maxLength={6}
										/>

										{errorMessage && <Notice message={errorMessage} variant="error" />}

										<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
											<Button disabled={isLoading || !authCodeForAuthCode} loading={isLoading} type="submit" variant="primary">
												Continuar
											</Button>

											<Button
												Icon={<ArrowLeft className="size-5" />}
												disabled={isLoading}
												type="button"
												onClick={() => (window.location.href = '/tiendas/eliminar-pago')}
												variant="secondary"
											>
												Atrás
											</Button>
										</div>
									</form>
								)}
							</div>
						</>
					)}

					{page === 4 && (
						<>
							{render_page_header('Eliminación Exitosa', '¡El pago ha sido eliminado correctamente!', <CircleCheck />)}

							<Button
								Icon={<ArrowLeft className="size-5" />}
								onClick={() => (window.location.href = '/tiendas/eliminar-pago')}
								variant="primary"
							>
								Eliminar otro pago
							</Button>
						</>
					)}
				</div>
			</div>
		</div>
	);

	function render_page_header(title: string, description: string, icon: React.ReactElement) {
		return (
			<div className="mb-4 flex flex-col items-center gap-2 md:mb-6 md:gap-3">
				<div className="relative">
					<div className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
					<div className="relative rounded-full bg-neutral-900 p-4 light:bg-neutral-1000 light:ring-2 light:ring-primary-500/10">
						{icon && React.cloneElement(icon, { className: 'h-6 w-6 text-primary-500' })}
					</div>
				</div>
				<div className="space-y-1 text-center">
					<h1 className="text-2xl font-semibold tracking-tighter text-neutral-0 md:text-3xl">{title}</h1>
					{description && <p className="text-neutral-300">{description}</p>}
				</div>
			</div>
		);
	}
};

export default DeletePayment;

